import React from 'react';
import { BarChart3, TrendingUp, FileText, Download, CheckCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/user/Navbar';
import Footer from '../../../components/user/Footer';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const AnalyticsReporting: React.FC = () => {
  const [heroRef, heroVisible] = useScrollAnimation();
  const [featuresRef, featuresVisible] = useScrollAnimation();
  const [benefitsRef, benefitsVisible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 pb-20 px-4 bg-gradient-to-br from-primary-50 to-white">
        <div className={`max-w-7xl mx-auto transition-all duration-700 ${
          heroVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`}>
          <Link 
            to="/services" 
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Services
          </Link>
          <div className="flex items-center gap-6 mb-6">
            <div className="bg-primary-100 p-4 rounded-2xl">
              <BarChart3 className="text-primary-600" size={64} />
            </div>
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-secondary-900 mb-4 tracking-tight">
                Analytics & Reporting
              </h1>
              <p className="text-xl text-secondary-600">
                Comprehensive analytics dashboard to track your business performance
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-secondary-900 mb-6">
                Make Data-Driven Decisions
              </h2>
              <p className="text-lg text-secondary-600 leading-relaxed mb-6">
                Our Analytics & Reporting platform provides comprehensive insights into your business 
                performance. Track sales trends, understand customer behavior, and identify opportunities 
                for growth with detailed reports and visualizations.
              </p>
              <p className="text-lg text-secondary-600 leading-relaxed">
                From real-time sales dashboards to detailed customer insights, our analytics tools help 
                you understand every aspect of your business. Export reports in PDF or Excel format 
                for accounting, presentations, or further analysis.
              </p>
            </div>
            <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-12 text-white shadow-soft">
              <TrendingUp size={48} className="mb-6" />
              <h3 className="text-2xl font-bold mb-4">Real-time Insights</h3>
              <p className="text-lg text-white/90 leading-relaxed">
                Monitor your business performance in real-time. Get instant updates on sales, 
                transactions, and customer activity as they happen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className="py-20 px-4 bg-secondary-50">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${
            featuresVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
            <h2 className="text-4xl font-bold text-secondary-900 mb-4">
              Key Features
            </h2>
            <p className="text-xl text-secondary-600">
              Powerful analytics tools for your business
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <BarChart3 size={40} />,
                title: 'Sales Analytics',
                description: 'Track sales performance with detailed charts and graphs. Analyze trends, peak hours, and revenue patterns.'
              },
              {
                icon: <TrendingUp size={40} />,
                title: 'Customer Insights',
                description: 'Understand your customers better. View demographics, purchase patterns, and customer lifetime value.'
              },
              {
                icon: <FileText size={40} />,
                title: 'Revenue Reports',
                description: 'Comprehensive revenue reports with breakdowns by product, service, time period, and payment method.'
              },
              {
                icon: <Download size={40} />,
                title: 'Export to PDF/Excel',
                description: 'Export your reports in multiple formats. Perfect for accounting, presentations, or further analysis.'
              },
              {
                icon: <BarChart3 size={40} />,
                title: 'Custom Date Ranges',
                description: 'Analyze data for any time period. Compare performance across days, weeks, months, or custom ranges.'
              },
              {
                icon: <TrendingUp size={40} />,
                title: 'Performance Metrics',
                description: 'Track key performance indicators (KPIs). Monitor growth, conversion rates, and business health metrics.'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-xl border-2 border-secondary-100 hover:border-primary-500 hover:shadow-soft transition-all duration-500 group hover:scale-105 hover:-translate-y-2 ${
                  featuresVisible 
                    ? `animate-fade-in-up opacity-100` 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-primary-500 mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-secondary-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={benefitsRef} className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${
            benefitsVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
            <h2 className="text-4xl font-bold text-secondary-900 mb-4">
              Why Use Our Analytics Platform?
            </h2>
            <p className="text-xl text-secondary-600">
              Benefits that help you grow smarter
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Data-Driven Decisions',
                description: 'Make informed business decisions based on real data, not guesswork. Understand what works and what doesn\'t.'
              },
              {
                title: 'Identify Opportunities',
                description: 'Spot trends and opportunities for growth. Discover peak hours, popular products, and customer preferences.'
              },
              {
                title: 'Track Performance',
                description: 'Monitor your business performance over time. Set goals and track progress toward achieving them.'
              },
              {
                title: 'Save Time',
                description: 'Automated reporting saves hours of manual work. Get comprehensive reports with a single click.'
              },
              {
                title: 'Better Planning',
                description: 'Use historical data to plan for the future. Forecast trends and prepare for busy periods.'
              },
              {
                title: 'Professional Reports',
                description: 'Generate professional reports for stakeholders, investors, or accounting purposes. Export in multiple formats.'
              }
            ].map((benefit, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-xl border-2 border-secondary-100 hover:border-primary-500 transition-all duration-500 ${
                  benefitsVisible 
                    ? `animate-fade-in-up opacity-100` 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <h3 className="text-xl font-bold text-secondary-900 mb-3 flex items-center">
                  <CheckCircle className="text-primary-500 mr-3" size={24} />
                  {benefit.title}
                </h3>
                <p className="text-secondary-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-20 px-4 bg-primary-500">
        <div className={`max-w-4xl mx-auto text-center text-white transition-all duration-700 ${
          ctaVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`}>
          <h2 className="text-4xl font-bold mb-6 tracking-tight">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Start tracking your business performance with comprehensive analytics
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="bg-white text-primary-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-secondary-50 transition-all duration-300 shadow-xl hover:scale-110 hover:-translate-y-1"
            >
              Contact Sales
            </Link>
            <Link
              to="/services"
              className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-primary-600 transition-all duration-300 hover:scale-110 hover:-translate-y-1"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AnalyticsReporting;

